import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Qué es la depresión de alto funcionamiento?",
    answer:
      "No es un diagnóstico formal, pero describe una depresión que pasa desapercibida porque la persona sigue funcionando. El equivalente clínico más cercano es el trastorno depresivo persistente (TDP): un estado de ánimo deprimido presente la mayor parte de los días durante al menos dos años.",
  },
  {
    question: "¿La depresión de alto funcionamiento se puede tratar?",
    answer:
      "Sí. Las opciones incluyen psicoterapia, antidepresivos y, para la depresión resistente al tratamiento, TMS o SAINT® TMS acelerada (autorizado por la FDA, K220177). Una evaluación psiquiátrica puede identificar el tratamiento apropiado.",
  },
];

export const esHighFunctioningDepression = makeEsArticle({
  slug: "high-functioning-depression-symptoms-treatment",
  title:
    "Depresión de alto funcionamiento: síntomas y tratamiento | Neuro Wellness",
  description:
    "La depresión de alto funcionamiento pasa desapercibida porque la persona sigue funcionando. Síntomas, el equivalente clínico (TDP) y opciones de tratamiento.",
  h1: "Depresión de alto funcionamiento: síntomas y tratamiento",
  breadcrumbLabel: "Depresión de alto funcionamiento",
  shortAnswer:
    "La 'depresión de alto funcionamiento' no es un diagnóstico formal, pero describe algo real: una depresión que pasa desapercibida porque la persona sigue cumpliendo con sus responsabilidades. El equivalente clínico más cercano es el trastorno depresivo persistente (TDP), antes llamado distimia: un estado de ánimo deprimido presente la mayor parte de los días durante al menos dos años en adultos. Es más leve que un episodio depresivo mayor pero dura mucho más. Las opciones de tratamiento incluyen psicoterapia, antidepresivos y, para la depresión resistente al tratamiento, TMS o SAINT® TMS acelerada (autorizado por la FDA, K220177).",
  keyTakeaways: [
    "La depresión de alto funcionamiento no es un diagnóstico formal.",
    "El equivalente clínico es el trastorno depresivo persistente (TDP).",
    "El TDP dura al menos dos años en adultos.",
    "Es más leve pero más duradero que un episodio depresivo mayor.",
    "Las opciones incluyen psicoterapia, antidepresivos, TMS y SAINT®.",
  ],
  sections: [
    {
      heading: "Cuando la depresión no parece depresión",
      paragraphs: [
        "Los síntomas que la mayoría imagina son los visibles: no levantarse de la cama, faltar al trabajo, una vida que claramente se ha detenido. Pero una forma más silenciosa de depresión parece casi lo opuesto: sigue respondiendo cada correo, asistiendo a cada reunión y cumpliendo cada compromiso. Por fuera, su vida parece bien.",
        "Precisamente por eso pasa desapercibida. Cuando sigue funcionando, la palabra 'bien' se estira para cubrir desde el bienestar real hasta un zumbido bajo de sufrimiento que lleva tanto tiempo que parece su personalidad.",
      ],
    },
    {
      heading: "Qué es la depresión de alto funcionamiento",
      paragraphs: [
        "La 'depresión de alto funcionamiento' no es un diagnóstico formal, pero describe algo real. El equivalente clínico más cercano es el trastorno depresivo persistente (TDP), antes llamado distimia: un estado de ánimo deprimido presente la mayor parte de los días durante al menos dos años en adultos. Suele ser más leve que un episodio depresivo mayor pero dura mucho más, a veces décadas.",
        "La característica definitoria es que sigue adelante. El trabajo se hace, las relaciones se mantienen, nada activa alarmas. Por debajo, hay un desgaste constante en el placer, la energía, la motivación y el compromiso emocional, lo bastante leve para sentirse normal.",
      ],
    },
    {
      heading: "Opciones de tratamiento",
      paragraphs: [
        "Las opciones incluyen psicoterapia y antidepresivos. Para la depresión resistente al tratamiento (falta de respuesta a al menos dos antidepresivos en dosis y duración adecuadas), la TMS está autorizada por la FDA y SAINT® es un protocolo de TMS acelerada de cinco días autorizado por la FDA (K220177). Una evaluación psiquiátrica puede identificar el tratamiento apropiado. [[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
